/**
 * Marquee scrolling text element
 * Creates infinite horizontal scrolling animation with configurable speed and pause on hover
 */

export class MarqueeElement extends HTMLElement {
  private animationId: number | null = null
  private scrollPosition: number = 0
  private content: HTMLElement | null = null
  private autoplaySpeed: number = 3000
  private isPaused: boolean = false
  private contentWidth: number = 0
  private resizeObserver: ResizeObserver | null = null
  private originalChildren: Node[] = []
  private isSetUp = false

  /**
   * The rAF loop only runs while the element is on screen, the document is visible,
   * and it is not paused — and it genuinely stops rather than idling. Every transform
   * write dirties the marquee's compositor layer, so an ungated loop repaints a
   * full-page-width layer on every frame indefinitely.
   */
  private visibilityObserver: IntersectionObserver | null = null
  private isOnScreen = false
  private handleDocumentVisibility = (): void => {
    this.syncAnimationState()
  }

  connectedCallback() {
    // setupMarquee() duplicates its own children to build the seamless loop, so it
    // must run exactly once. Re-running it on reconnect (the element being moved in
    // the DOM, e.g. as a carousel cell) would treat the previous wrapper as original
    // content and clone that too, doubling the subtree on every move.
    if (this.isSetUp) {
      this.calculateContentWidth()
      // Restore the observer disconnectedCallback dropped, or width recalculation
      // stays dead after a reconnect (`setupMarquee` won't run again).
      this.setupResizeObserver()
    } else {
      this.setupMarquee()
    }

    this.setupVisibilityGate()
    document.addEventListener('visibilitychange', this.handleDocumentVisibility)
    this.syncAnimationState()
  }

  disconnectedCallback() {
    this.stopAnimation()
    this.resizeObserver?.disconnect()
    this.visibilityObserver?.disconnect()
    this.visibilityObserver = null
    this.isOnScreen = false
    document.removeEventListener('visibilitychange', this.handleDocumentVisibility)
  }

  /** Runs the animation only while the marquee is actually visible to the shopper. */
  private setupVisibilityGate() {
    if (!('IntersectionObserver' in window)) {
      // No gate available — fall back to always-on.
      this.isOnScreen = true
      return
    }

    this.visibilityObserver?.disconnect()
    this.visibilityObserver = new IntersectionObserver(
      ([entry]) => {
        this.isOnScreen = entry.isIntersecting
        this.syncAnimationState()
      },
      // A little margin so it is already moving by the time it scrolls into view.
      { rootMargin: '200px' }
    )
    this.visibilityObserver.observe(this)
  }

  /** Single place that decides whether the rAF loop should be running. */
  private syncAnimationState() {
    const shouldRun = this.isOnScreen && !this.isPaused && !document.hidden

    if (shouldRun) {
      this.startAnimation()
    } else {
      this.stopAnimation()
    }
  }

  private setupMarquee() {
    this.isSetUp = true

    // Get autoplay speed from data attribute
    const speedAttr = this.getAttribute('data-autoplay-speed')
    if (speedAttr) {
      this.autoplaySpeed = parseInt(speedAttr, 10) || 3000
    }

    // Store original children
    this.originalChildren = Array.from(this.childNodes)

    // Create content wrapper that will hold original + cloned content
    this.content = document.createElement('div')
    this.content.style.cssText = `
      display: inline-flex;
      align-items: center;
      white-space: nowrap;
      min-width: max-content;
    `

    // Move original children to content wrapper
    this.originalChildren.forEach(child => {
      this.content!.appendChild(child)
    })

    // Clone the content for seamless loop
    this.originalChildren.forEach(child => {
      const clone = child.cloneNode(true)
      this.content!.appendChild(clone)
    })

    // Add content to element
    this.appendChild(this.content)

    // Setup resize observer to recalculate content width when content changes
    this.setupResizeObserver()

    // Calculate initial content width after a brief delay to ensure DOM is rendered
    setTimeout(() => this.calculateContentWidth(), 0)

    // Pause on hover
    this.addEventListener('mouseenter', () => this.pause())
    this.addEventListener('mouseleave', () => this.resume())
  }

  private setupResizeObserver() {
    if ('ResizeObserver' in window) {
      this.resizeObserver?.disconnect()
      this.resizeObserver = new ResizeObserver(() => {
        this.calculateContentWidth()
      })

      if (this.content) {
        this.resizeObserver.observe(this.content)
      }
    }
  }

  private calculateContentWidth() {
    if (this.content && this.originalChildren.length > 0) {
      // Half the total content = original children only. getBoundingClientRect, not
      // scrollWidth: the transform is sub-pixel, so the wrap point must be too.
      this.contentWidth = this.content.getBoundingClientRect().width / 2

      // A re-measure can shrink the width below the current offset — clamp it or
      // the loop animates past the wrap point and the two copies overlap.
      if (this.scrollPosition >= this.contentWidth) {
        this.scrollPosition = this.contentWidth > 0 ? this.scrollPosition % this.contentWidth : 0
        this.content.style.transform = `translateX(-${this.scrollPosition}px)`
      }
    }
  }

  private startAnimation() {
    if (this.animationId) return

    // `will-change` is applied only while actually animating: left on permanently it
    // pins a compositor layer for every marquee whether or not anything is moving.
    if (this.content) this.content.style.willChange = 'transform'

    const animate = () => {
      // Stop, do not idle — `syncAnimationState` restarts the loop when conditions change.
      if (this.isPaused || !this.isOnScreen || document.hidden) {
        this.animationId = null
        if (this.content) this.content.style.willChange = 'auto'
        return
      }

      if (this.content && this.contentWidth > 0) {
        // Calculate speed based on autoplay speed (lower number = faster)
        const speedMultiplier = Math.max(0.2, 3000 / this.autoplaySpeed)
        this.scrollPosition += speedMultiplier
        this.content.style.transform = `translateX(-${this.scrollPosition}px)`

        // Wrap by subtracting, not snapping to 0 — a long frame can overshoot the
        // wrap point, and discarding the remainder jumps the track backwards.
        if (this.scrollPosition >= this.contentWidth) {
          this.scrollPosition -= this.contentWidth
        }
      }

      this.animationId = requestAnimationFrame(animate)
    }

    this.animationId = requestAnimationFrame(animate)
  }

  private stopAnimation() {
    if (this.animationId) {
      cancelAnimationFrame(this.animationId)
      this.animationId = null
    }
    // Release will-change here too — syncAnimationState cancels the frame before
    // the rAF terminal branch can do it, which would pin the compositor layer.
    if (this.content) this.content.style.willChange = 'auto'
  }

  private pause() {
    this.isPaused = true
    this.syncAnimationState()
  }

  private resume() {
    this.isPaused = false
    this.syncAnimationState()
  }

  // Public methods for external control
  public pauseMarquee() {
    this.pause()
  }

  public resumeMarquee() {
    this.resume()
  }

  public setSpeed(speed: number) {
    this.autoplaySpeed = speed
  }

  public recalculateWidth() {
    this.calculateContentWidth()
  }
}
